import { useEffect, useState } from 'react';

export function useIsMobile(): boolean {
  const [mobile, setMobile] = useState(
    () => typeof window !== 'undefined' && window.matchMedia('(max-width: 768px)').matches,
  );
  useEffect(() => {
    const mq = window.matchMedia('(max-width: 768px)');
    const fn = (e: MediaQueryListEvent) => setMobile(e.matches);
    mq.addEventListener('change', fn);
    return () => mq.removeEventListener('change', fn);
  }, []);
  return mobile;
}

export const COLOR = {
  orange: '#fdd663',
  orangeDim: '#e37400',
  blue: '#80e9ff',
  blueDim: '#253255',
  green: '#3ecf8e',
  red: '#ff6b81',
  amber: '#fdd663',
  diffAdded: '#81c995',
  diffRemoved: '#f28b82',
  diffAddedBg: 'rgba(129,201,149,0.12)',
  diffRemovedBg: 'rgba(242,139,130,0.12)',
  textPrimary: '#f6f9fc',
  textSecondary: '#adbdcc',
  textMuted: '#8898aa',
  textInactive: '#65738f',
  bg: '#0b1020',
  bgRaised: '#11182a',
  bgOverlay: '#17213a',
  border: '#34415f',
  borderSubtle: '#26324d',
} as const;

// CC's glyph vocabulary
export const G = {
  // Tool card structure
  responsePrefix: '⎿',              // U+23BE — output left-border connector
  bullet:         '●',              // U+25CF — filled circle (status dot)
  bulletOpen:     '◇',             // U+25C7 — open diamond (queued)
  bulletDone:     '◆',             // U+25C6 — filled diamond (complete)
  dot:            '∙',             // U+2219 — sub-item bullet

  // Arrows
  arrowUp:        '↑',
  arrowDown:      '↓',
  arrowRight:     '→',

  // Misc
  warning:        '⚠',
  lock:           '🔒',
  stop:           '■',
} as const;

export const FONT = {
  mono:  "'Roboto Mono','JetBrains Mono','Monaco','Menlo',monospace",
  sans:  "'Google Sans','Roboto',Arial,sans-serif",
  size:  {
    xs:   '0.75rem',
    sm:   '0.875rem',
    base: '0.95rem',
    md:   '1rem',
  },
  lineHeight: { tight: 1.4, base: 1.55, relaxed: 1.7 },
} as const;

export const ANIM = {
  pulse:   'nora-pulse 1.2s ease-in-out infinite',
  blink:   'nora-blink 1s step-start infinite',
  spin:    'nora-spin 0.8s linear infinite',
  fadeIn:  'nora-fadein 0.15s ease',
  slideIn: 'nora-slidein 0.2s ease',
} as const;

/** Inject global keyframes once */
export function injectGlobalStyles() {
  if (document.getElementById('nora-global-styles')) return;
  const el = document.createElement('style');
  el.id = 'nora-global-styles';
  el.textContent = `
    @keyframes nora-pulse  { 0%,100%{opacity:1} 50%{opacity:0.25} }
    @keyframes nora-blink  { 0%,100%{opacity:1} 50%{opacity:0} }
    @keyframes nora-fadein { from{opacity:0} to{opacity:1} }
    @keyframes nora-slidein{ from{transform:translateX(20px);opacity:0} to{transform:translateX(0);opacity:1} }

    .nora-scroll::-webkit-scrollbar { width: 4px; height: 4px; }
    .nora-scroll::-webkit-scrollbar-track { background: transparent; }
    .nora-scroll::-webkit-scrollbar-thumb { background: #737679; border-radius: 4px; }

    .nora-prose pre  { margin: 0; }
    .nora-prose code { font-family: ${FONT.mono}; font-size: 0.82em; }
    .nora-prose p    { margin: 0 0 8px; }
    .nora-prose p:last-child { margin-bottom: 0; }
    .nora-prose ul,
    .nora-prose ol   { margin: 0 0 8px; padding-left: 20px; }
    .nora-prose li   { margin-bottom: 2px; }
    .nora-prose h1,
    .nora-prose h2,
    .nora-prose h3   { margin: 12px 0 6px; font-weight: 700; }
    .nora-prose blockquote {
      margin: 0 0 8px; padding: 6px 12px;
      border-left: 3px solid #7a73ff; color: #adbdcc;
    }
    .nora-prose table { border-collapse: collapse; width: 100%; margin-bottom: 8px; }
    .nora-prose th,
    .nora-prose td    { border: 1px solid #34415f; padding: 4px 10px; font-size: 0.82rem; }
    .nora-prose th    { background: #17213a; font-weight: 600; }
    .nora-prose a     { color: #80e9ff; }

    @keyframes nora-spin { to { transform: rotate(360deg); } }

    /* CC-style code blocks — class names match markdownRenderer.ts output */
    .nora-code-block {
      position: relative; margin: 6px 0;
      background: #07111f; border: 1px solid #34415f; border-radius: 8px;
      overflow: hidden;
    }
    .nora-code-header {
      display: flex; align-items: center; justify-content: space-between;
      padding: 4px 12px;
      background: #17213a; border-bottom: 1px solid #34415f;
      font-family: ${FONT.mono}; font-size: 0.72rem; color: #adbdcc;
    }
    .nora-copy-btn {
      display: flex; align-items: center; gap: 4px;
      cursor: pointer; background: transparent; border: none;
      color: #adbdcc; font-family: ${FONT.mono}; font-size: 0.72rem;
      padding: 2px 6px; border-radius: 4px; transition: color 0.15s;
    }
    .nora-copy-btn:hover { color: #f0f0f0; }
    .nora-code-block pre {
      margin: 0; padding: 12px;
      font-family: ${FONT.mono}; font-size: 0.82rem; line-height: 1.55;
      color: #f0f0f0; overflow-x: auto;
      white-space: pre; tab-size: 2;
    }
  `;
  document.head.appendChild(el);
}
